// Starting values for balancing. Every number here is meant to be tuned
// with the balance simulation (pnpm sim) and live metrics.

/**
 * Krug 5 (F2): caps on effects that stack from many sources (affixes, perks, sets, skills,
 * weapon class). Each cap holds for the sum of all sources.
 */
export const STACK_CAPS = {
  /** Accuracy bonus: at most +20 percentage points on the hit chance. */
  accuracy: 0.2,
  /** Critical hit chance, "+crit against stronger targets" included. */
  crit: 0.75,
  /** Extra hit (Burst) plus extra attack (Kickback): at most 25% together. */
  extraStrikes: 0.25,
  /** Defense a hit can ignore: perks plus the melee two-handed rule. */
  ignoreDefense: 0.5,
  /** Defense taken off the target: "target has less defense" plus crit shred (Inferno). */
  defenseCut: 0.4,
} as const;

/**
 * Krug 5 (K13): how random a fight is, in one place. Every fight goes through `simulateCombat`
 * (NPC fights, PvP, ghosts, ambushes, the horde, the encounter estimate and the simulations).
 * Simulations can try other values for one run: `COMBAT=dmgSpread=0.25,pvpDamageMult=1.7`.
 */
/**
 * Krug 5 (K17): energy is a daily budget that limits only whoever plays far more than most; every
 * value in one place (it replaces GAME.ENERGY_* and HORDE_ENERGY). `legacy` is the rule before K17 for
 * the sims (`useLegacyEnergy`, NO_K17=1): +1 every 30 minutes, a run costs its zone's energy (the first
 * two runs and raids too), a horde wave 2.
 */
export const ENERGY_RULES = {
  /**
   * The most a survivor holds under the rule of 1. 10. 2026 (C5): 20 on every level. Since the owner's decision of
   * 7. 10. 2026 the maximum goes by the Medical Tent (`maxByTent`); this stays for the sims (`useEnergyBefore7Oct`).
   */
  max: 20,
  /** +1 every 3 hours on every level (8 a day; owner, 7. 10. 2026; before: every 2 hours, C5); it fills while the survivor is out too. */
  regenMinutes: 180,
  /**
   * Simulations only (`useEnergyBefore1Oct`, VARIANT=a): the rules of 30. 9. 2026, a maximum of 9 + level
   * and +1 every 4 hours from level 10. Null in the game: one rule for every level.
   */
  perLevel: null as null | { maxPlus: number; slowFrom: number; slowMinutes: number },
  /** A run by the button's base duration: up to N minutes costs M (longer: the last row). */
  byDuration: [
    [60, 1],
    [120, 2],
    [240, 3],
    [480, 5],
  ] as [number, number][],
  /** The first two runs (onboarding). */
  onboarding: 0,
  raid: 1,
  hordeWave: 1,
  /**
   * Rewards may go over the maximum: the ad, a friend's gift (the daily reward's day 4 is in DAILY_REWARDS). 23. 10. 2026
   * (owner, pilot report): the ad 2 -> 1, all three contracts 3 -> 2, the daily reward's day 4 and the season's energy goals
   * 5 -> 4, so a player of about 18 runs a day is out of energy in 10–20% of sessions (a replay of the real players: 13% for
   * the one at 17 runs a day, 0% for the others); the refill and the maximum stay.
   */
  ad: 1,
  friendGift: 1,
  /** 1. 10. 2026 (C2): every building level finished (built or upgraded), once per level. */
  building: 2,
  /** 1. 10. 2026 (C2): claiming all three daily contracts, on top of the bonus item (23. 10. 2026: 3 -> 2). */
  allContracts: 2,
  /** K17.2: the energy ad is offered and taken only below this. */
  adNeededBelow: 5,
  legacy: false,
  /**
   * 7. 10. 2026 (owner's decision after the test): the maximum by the Medical Tent, `base` + `perLevel` a tent level (10,
   * 12 … 20 on level 5); rewards may go over it. Null: `max` on every level (the rule of 1. 10., sims only).
   */
  maxByTent: { base: 10, perLevel: 2 } as null | { base: number; perLevel: number },
  /** 7. 10. 2026 (energy test): a faster refill in the account's first `days` days; null: none (the days 1–3 needed none). */
  firstDays: null as null | { days: number; regenMinutes: number },
};

/**
 * 7. 10. 2026 (owner, energy test; ENERGY_NEW=1 in the sims): +1 every 3 hours, the maximum 10 + 2 a Medical Tent level;
 * `fastFirstDays` (FAST3=1): the account's first days refill faster.
 */
export function useEnergyByTent(o: { regenMinutes?: number; base?: number; perLevel?: number; fastFirstDays?: { days: number; regenMinutes: number } | null } = {}) {
  Object.assign(ENERGY_RULES, { regenMinutes: o.regenMinutes ?? 180, maxByTent: { base: o.base ?? 10, perLevel: o.perLevel ?? 2 }, firstDays: o.fastFirstDays ?? null });
}

/** NO_K17=1 (sims only): the energy rules from before K17. */
export function useLegacyEnergy() {
  Object.assign(ENERGY_RULES, { max: 10, regenMinutes: 30, perLevel: null, hordeWave: 2, legacy: true, maxByTent: null, firstDays: null });
}

/** ENERGY_OLD=1 (sims only): the rule of 1. 10. 2026 to 7. 10. 2026 (C5): a maximum of 20 and +1 every 2 hours on every level. */
export function useEnergyBefore7Oct() {
  Object.assign(ENERGY_RULES, { max: 20, regenMinutes: 120, perLevel: null, maxByTent: null, firstDays: null });
}

/** VARIANT=a (sims only): the energy of 30. 9. 2026: maximum 9 + level, +1 every 2 h below level 10 and every 4 h from it. */
export function useEnergyBefore1Oct() {
  Object.assign(ENERGY_RULES, { max: 10, regenMinutes: 120, perLevel: { maxPlus: 1, slowFrom: 10, slowMinutes: 240 }, maxByTent: null, firstDays: null });
}

/**
 * 29. 9. 2026: the reward for linking a guest account to an email or Google, once per account
 * (`users.flags.linkReward`): these stacks (may go over Storage) and one item of this rarity.
 */
/**
 * The landing page (29. 9. 2026): the line "N survivors so far · N expeditions today" shows only once
 * both are real enough to be worth saying (the big "out there" number stays, O3).
 */
export const LANDING_RULES = { totalsMinSurvivors: 100, totalsMinRunsToday: 50 };

export const LINK_REWARD = { stacks: { bandage: 3, scrap: 50, wood: 20, metal: 20 }, rarity: 'uncommon', label: '3 Bandages, 50 Scrap, 20 Wood, 20 Metal' } as const;

export const COMBAT_RULES = {
  /** A hit's damage × U(1 − s, 1 + s) (0.15 before K13). */
  dmgSpread: 0.3,
  /** Damage when both sides are survivors (PvP, ghosts, ambushes); not NPC fights or the horde (1 before K13). */
  pvpDamageMult: 3,
  /** Each side's initiative × U(1 − r, 1 + r), rolled once at the start of the fight (0 before K13). */
  initiativeRoll: 0.1,
};
export type CombatRules = typeof COMBAT_RULES;

/** `dmgSpread=0.25,pvpDamageMult=1.7,initiativeRoll=0.1` → the rules for this run (simulations only). */
export function setCombatRules(spec: string | null | undefined): CombatRules {
  for (const pair of (spec ?? '').split(',').filter(Boolean)) {
    const [k, v] = pair.split('=').map((x) => x.trim());
    if (!(k in COMBAT_RULES) || !Number.isFinite(Number(v))) throw new Error(`COMBAT: unknown rule or value "${pair}"`);
    COMBAT_RULES[k as keyof CombatRules] = Number(v);
  }
  return COMBAT_RULES;
}

export const GAME = {
  LEVEL_CAP: 20,
  BASE_ATTR: 15,
  ATTR_POINTS_PER_LEVEL: 3,
  SKILL_POINTS_PER_LEVEL: 1,
  /** Attribute points above this count half (krug 5, F8.2). */
  ATTR_SOFT_CAP: 40,

  /** B6 (1. 10. 2026): two kinds of ad, each once a day (energy, full heal); the Trader's ad refresh is gone. */
  ADS_PER_DAY: 2,
  /** Squad bonus (krug 3, L8 / O12): both friends, for every tick they are out in the same zone. */
  SQUAD_LOOT: 0.1,
  SQUAD_XP: 0.05,
  /** Horde "Top 10%": counted among real survivors only, Militia never take a place (krug 3, T2). */
  HORDE_TOP_SHARE: 0.1,

  UNARMED_DAMAGE: 6,
  /** A loud weapon (firearms, the chainsaw): less stealth and more fights on the road. */
  LOUD_STEALTH_PENALTY: 0.05,
  LOUD_COMBAT_WEIGHT: 1.15,
  CARRY_BASE: 10,

  /** Camp HP regeneration, fraction of max HP per hour (4 h from empty to full). */
  CAMP_REGEN_PER_HOUR: 0.25,
  /** Each Medical Tent level adds this much to camp regeneration. */
  MEDICAL_TENT_REGEN_PER_LEVEL: 0.12,
  MIN_HP_TO_DEPART: 0.15,

  HUNGRY_ATTACK_PENALTY: 0.15,

  MAX_COMBAT_ROUNDS: 10,
  AMBUSH_DAMAGE_BONUS: 0.25,
  BASE_CRIT: 0.05,
  CRIT_MULT: 1.5,
  HIT_BASE: 0.75,
  HIT_MIN: 0.4,
  HIT_MAX: 0.95,
  ESCAPE_BASE: 0.5,
  ESCAPE_MIN: 0.1,
  ESCAPE_MAX: 0.9,
  ESCAPE_FULL_PACK_PENALTY: 0.2,
  DETECT_BASE: 0.3,
  DETECT_MIN: 0.05,
  DETECT_MAX: 0.8,
  MANUAL_DECISION_BONUS: 0.1,

  MAX_PVP_PER_EXPEDITION: 2,
  PVP_POWER_RANGE: 0.25,
  PVP_PROTECTION_AFTER_LOSS_MINUTES: 90,
  NEW_PLAYER_PROTECTION_LEVEL: 5,
  NEW_PLAYER_PROTECTION_DAYS: 3,
  PVP_LOSS_MIN: 0.05,
  PVP_LOSS_MAX: 0.15,
  GHOST_LOSS_MIN: 0.025,
  GHOST_LOSS_MAX: 0.075,
  GHOST_LOSSES_PER_DAY: 2,
  GHOST_TTL_MIN_HOURS: 48,
  GHOST_TTL_MAX_HOURS: 72,
  REVENGE_TOKEN_HOURS: 24,
  WANTED_HOURS: 24,
  WANTED_WEAK_RATIO: 0.7,
  WANTED_THRESHOLD: 3,
  ENCOUNTER_PROMPT_SECONDS: 90,

  RESPEC_SCRAP_COST: 100,

  // 11. 10. 2026 (owner, BACKLOG #33): a crew has at most 5 members and costs 1,000 Scrap (was 25 and 200; `CREW_RULES`).
  CLAN_MAX_MEMBERS: 5,
  CLAN_CREATE_SCRAP: 1000,
  CLAN_ZONE_LOOT_BONUS: 0.1,
  CLAN_MAX_ZONES: 2,
  RAID_HOURS: 24,
  RAID_PROTECTION_HOURS: 72,
  RAID_EXPEDITION_MINUTES: 20,

  HORDE_MINUTES: 60,
  HORDE_WAVE_MINUTES: 15,
  HORDE_WAVE_TICKS: 5,
  HORDE_VICTORY_LOOT_BONUS: 0.25,
  HORDE_VICTORY_HOURS: 24,
  /** A horde that wins: the zone's danger rises and its loot thins for a while (29. 9. 2026: from horde.ts). */
  HORDE_DEFEAT_DANGER: 1,
  HORDE_DEFEAT_LOOT: 0.15,
  HORDE_DEFEAT_HOURS: 72,
  /** How long after the end the horde screen keeps its rewards to claim. */
  HORDE_CLAIM_HOURS: 18,
  /** Length of a season (29. 9. 2026: from world.ts). */
  SEASON_DAYS: 49,
  /** A lost fight: the chance the winner also takes one Common or Uncommon item from the pack (from encounters.ts). */
  PVP_ITEM_LOSS_CHANCE: 0.2,
  /** XP of a run while the survivor's level is below the server average, rounded down (from expeditions.ts). */
  RESTED_XP_MULT: 1.5,
  /** Days missed in a row that start the daily reward cycle over (from camp.ts). */
  DAILY_MISS_RESET_DAYS: 2,

  RUMOR_DISCOVERY_BONUS: 0.5,
  RUMOR_HOURS: 8,

  DAILY_CYCLE: 7,

  STORY_SHARE_BASE_URL: 'lastday.gg',
} as const;

/** Krug 5 (F5): levels 1–4 need 50 / 100 / 150 / 200 XP (was 70 / 140 / 230 / 320), so level 5 comes in 1–2 days. */
const EARLY_XP = [0, 50, 100, 150, 200];

/**
 * Levels 10–19 take the curve times a multiplier. Krug 5, K4.6: a flat ×3 (with loot left behind
 * instead of a full pack, runs play out and level 20 came in about 20 days). K5.3: a growing one,
 * `from` on level 10 up to `to` on level 19, `from + (to − from) × ((level − 10) / 9)^power`, so a
 * player of one or two sessions a day still gets levels after 10 while an active one reaches 20
 * late in the season (7 weeks). Levels 1–9 stay as they are. Mutable only for the simulations.
 * K15.1: the top ×6 → ×7.5 (with the simulation's rested fixed, active players reached 20 in 37–39
 * days; now 42–44, the season is 49).
 * 3. 10. 2026 (package 2, owner decision: the mildest end that keeps the pace): ×1 → ×8.2 with power 0.8
 * instead of ×7.5 with power 2. Long runs and the night run give +40–65% XP a day, so levels 11–19 all ask
 * more (×1.1–2.1 of before, nothing less); the last level takes at most twice the days of 15 → 16 (power 2:
 * about three times). `useRulesBefore3Oct` (package2.ts) puts the old curve back for the simulations.
 * 4. 10. 2026 (package 3, part 0: the simulation plays like a real player, the longest run it can pay for in the most
 * dangerous zone of its level): the top ×8.2 → ×11, power 0.8 stays, so active players reach 20 on days 35–38 (owner:
 * the upper end of 32–38, the simulation is still a little slower than a real player). Survivors keep their level and
 * their XP inside it. `useRulesBefore4Oct` (package3.ts) puts ×8.2 back for the simulations.
 */
export const XP_CURVE = { lateFrom: 10, lateTo: 19, from: 1, to: 11, power: 0.8 };

/** The multiplier on the XP curve for a level (1 below level 10). */
export function lateXpMult(level: number): number {
  if (level < XP_CURVE.lateFrom) return 1;
  const k = Math.min(1, (level - XP_CURVE.lateFrom) / (XP_CURVE.lateTo - XP_CURVE.lateFrom));
  return XP_CURVE.from + (XP_CURVE.to - XP_CURVE.from) * k ** XP_CURVE.power;
}

/** XP needed to go from `level` to `level + 1`. 1,440 at level 12 on the base curve, as in the style brief. */
export function xpToNext(level: number): number {
  if (level >= GAME.LEVEL_CAP) return 0;
  if (level < EARLY_XP.length) return EARLY_XP[level];
  const base = Math.round((60 * level + 5 * level * level) / 10) * 10;
  return level >= XP_CURVE.lateFrom ? Math.round((base * lateXpMult(level)) / 10) * 10 : base;
}

export function totalXpForLevel(level: number): number {
  let sum = 0;
  for (let l = 1; l < level; l++) sum += xpToNext(l);
  return sum;
}

export const DANGER_WORDS = ['Quiet', 'Uneasy', 'Hostile', 'Lethal', "Don't"] as const;
